import { MenuOutlined } from '@ant-design/icons';
import { Transfer, type TransferProps } from 'antd';
import { useField } from 'formik';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormItem, { type FormItemProps } from './FormItem';

const ItemWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    > .label {
        display: inline-block;
        max-width: calc(100% - 20px);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
    &.drop-over-downward {
        border-bottom: 2px dashed #1890ff;
    }
    &.drop-over-upward {
        border-top: 2px dashed #1890ff;
    }
`;

const StyledTransfer = styled(Transfer)`
    .ant-transfer-operation > .ant-btn + .ant-btn {
        margin-left: 0;
    }
`;

export interface TransferFieldProps<RecordType> extends Omit<TransferProps<RecordType>, 'onChange' | 'targetKeys'> {
    name: string;
    required?: boolean;
    label?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    preprocessTargetKeys?: (input: string[]) => string[];
    dataSource: any[];
    setDataSource: Dispatch<SetStateAction<any[]>>;
}

type DraggableItemProps = {
    currentIndex: any;
    label: string;
    moveRow: (prevIndex, currentIndex) => Promise<void>;
};

type DragObject = {
    index: number;
    label: string;
};

const DraggableItem = ({ currentIndex, label, moveRow }: DraggableItemProps) => {
    const ref = useRef();
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: type,
        collect: monitor => {
            const { index: dragIndex } = monitor.getItem() || { index: currentIndex };
            if (dragIndex === currentIndex) {
                return {};
            }

            return {
                isOver: monitor.isOver(),
                dropClassName: dragIndex < currentIndex ? ` drop-over-downward` : ` drop-over-upward`,
            };
        },
        drop: (item: DragObject) => {
            moveRow(item.index, currentIndex);
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type,
        item: { index: currentIndex },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });

    preview(drop(ref));

    return (
        <ItemWrapper key={label} ref={ref} className={isOver ? dropClassName : ''}>
            <span className="label">{label}</span>
            {currentIndex !== -1 && (
                <span ref={drag}>
                    <MenuOutlined />
                </span>
            )}
        </ItemWrapper>
    );
};

const type = 'DraggableItem';

const SortableTransferField = <RecordType extends object = any>({
    name,
    required,
    label,
    itemProps,
    preprocessTargetKeys,
    titles,
    ...props
}: TransferFieldProps<RecordType>) => {
    const { t } = useTranslation(['common']);

    const usedTitles = useMemo(
        () => titles ?? [t('common:transfer.unassigned'), t('common:transfer.assigned')],
        [t, titles]
    );

    const [{ value: selectedKeys }, meta, { setValue }] = useField({ name });
    const [targetKeys, setTargetKeys] = useState(selectedKeys);

    const onUpdate = useCallback(
        (targetKeys: string[]) => {
            setValue(targetKeys);
            setTargetKeys(targetKeys);
        },
        [setTargetKeys, setValue]
    );

    const onChange = useCallback(
        (targetKeys: string[]) => {
            // make the new selected keys to be at bottom of selected pool
            const hasNewKeys = targetKeys.length > selectedKeys.length;
            const newKeys = hasNewKeys
                ? selectedKeys.concat(targetKeys.slice(0, targetKeys.length - selectedKeys.length))
                : targetKeys;

            onUpdate(newKeys);
        },
        [preprocessTargetKeys, selectedKeys, onUpdate]
    );

    const [targetSelectedKeys, setTargetSelectedKeys] = useState([]);
    const onSelectChange = useCallback((sourceSelectedKeys, targetSelectedKeys) => {
        setTargetSelectedKeys(targetSelectedKeys);
    }, []);

    // change order
    const moveRow = useCallback(
        async (dragIndex, hoverIndex) => {
            const newList = (() => {
                const dragIndexes = targetSelectedKeys.length
                    ? targetSelectedKeys.map(selectedKey => targetKeys.findIndex(key => key === selectedKey)).toSorted()
                    : [dragIndex];

                const dragKeys = dragIndexes.map(index => targetKeys[index]);
                const rest = targetKeys.filter(key => !dragKeys.includes(key));
                if (hoverIndex === 0) {
                    // insert to top
                    return [...dragKeys, ...rest];
                }

                if (hoverIndex === targetKeys.length - 1) {
                    // insert to bottom
                    return [...rest, ...dragKeys];
                }
                // insert to center
                const isDown = hoverIndex > dragIndexes[0];

                const hoverKey = targetKeys[hoverIndex];
                const realHoverIndex = isDown
                    ? rest.findIndex(key => key === hoverKey) + 1
                    : rest.findIndex(key => key === hoverKey);

                rest.splice(realHoverIndex, 0, ...dragKeys);

                return rest;
            })();

            onUpdate(newList);
        },
        [targetKeys, onUpdate, targetSelectedKeys]
    );

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required}>
            <DndProvider backend={HTML5Backend}>
                <StyledTransfer
                    {...props}
                    onChange={onChange}
                    onSelectChange={onSelectChange}
                    render={it => (
                        <DraggableItem
                            currentIndex={targetKeys.findIndex(key => key === it.key)}
                            label={it.title}
                            moveRow={moveRow}
                        />
                    )}
                    targetKeys={selectedKeys}
                    titles={usedTitles}
                />
            </DndProvider>
        </FormItem>
    );
};

export default SortableTransferField;
